import { useContext, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnFiltersState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pushpress/pushpress-ui";
import {
  useGetUserOutstandingInvoices,
  useGetUserPaymentHistory,
} from "../../../../../apis/billingApi.hooks";
import {
  BILLING_TYPE,
  BillingType,
  Invoice,
  PaymentHistoryItem,
} from "../../../../../apis/billingApi";
import EmptyTable from "../../shared/EmptyTable";
import TablePagination from "../../shared/TablePagination";
import ErrorState from "../../shared/ErrorState";
import TableSkeleton from "../../shared/TableSkeleton";
import { AppContext } from "../../../../../context/app";
import { invoiceColumns, paymentColumns } from "./columns";
import { formatCurrency } from "../../../../../utils/currencyUtils";
import { PAGINATION_PAGE_SIZE } from "../../../constants";
import { convertDateStringOrTimestampToUsDateFormat } from "../../../helpers";
import { ViewMode } from "./Billing";

type TableRow = {
  type: "invoice" | "payment";
  invoiceUuid: string;
  billingId: number;
  openTimestamp: string;
  nextAttemptTimestamp: string;
  status: PaymentHistoryItem["status"];
  transactionLogUuid?: string;
  receiptId?: number;
};

const getBillingType = (billingType: BillingType, invoice: Invoice) => {
  /* Not all billing types are represented in the current PHP page.
   * We might need to amend this in the future.
   */
  if (billingType === BILLING_TYPE.CASH) return "Cash/Check";
  if (billingType === BILLING_TYPE.BANK_ACCOUNT) return "Bank Account";
  if (billingType === BILLING_TYPE.COMP) return "Comp";
  if (billingType === BILLING_TYPE.STRIPE_LINK) return "Stripe Link";
  if (billingType === BILLING_TYPE.CREDIT_CARD) {
    return `${invoice.billingBrand} (${invoice.billingLastFour})`;
  }
  if (billingType === BILLING_TYPE.AU_BECS_DEBIT) {
    if (invoice.billingLastFour) {
      return `${invoice.billingBrand} (${invoice.billingLastFour}) ${!invoice.isVerified ? "unverified" : ""}`;
    }
    return `${invoice.billingBrand} (pending)`;
  }
  return "";
};

const getClickablePaymentLink = (
  status: PaymentHistoryItem["status"],
  receiptId: number,
  invoiceUuid: string,
  transactionLogUuid: string,
) => {
  if (status === "failure" || status === "pending") {
    return `/invoices/transaction/${transactionLogUuid}`;
  }
  if (invoiceUuid) {
    return `/invoices/invoice/${invoiceUuid}`;
  }
  return `/reports/receipt/${receiptId}`;
};

const getEmptyTableMessage = (viewMode: string) => {
  if (viewMode === "payments") {
    return "No payments for this user";
  }
  if (viewMode === "pending") {
    return "No pending invoices for this user";
  }
  return "No invoices for this user";
};

type BillingHistoryTableProps = {
  userUuid: string;
  columnFilters: ColumnFiltersState;
  viewMode: ViewMode;
};

export default function BillingHistoryTable({
  userUuid,
  columnFilters,
  viewMode,
}: BillingHistoryTableProps) {
  const app = useContext(AppContext);
  const { client } = app;

  const {
    data: outstandingInvoicesData,
    isFetching: isFetchingOutstandingInvoices,
    error: errorOutstandingInvoices,
    refetch: refetchOutstandingInvoices,
  } = useGetUserOutstandingInvoices(userUuid);

  const {
    data: paymentHistoryData,
    isFetching: isFetchingPaymentHistory,
    error: errorPaymentHistory,
    refetch: refetchPaymentHistory,
  } = useGetUserPaymentHistory(userUuid);

  const convertToTableData = (
    outstandingInvoicesData: Invoice[],
    paymentHistoryData: PaymentHistoryItem[],
    currencyIso: string,
  ): TableRow[] => {
    let invoices = [];
    let payments = [];

    if (outstandingInvoicesData?.length) {
      invoices = outstandingInvoicesData.map((invoice) => {
        const {
          invoiceUuid,
          openTimestamp,
          nextAttemptTimestamp,
          billingLastFour,
          billingBrand,
          total,
          isPending,
          isVerified,
          billingType,
        } = invoice;
        return {
          type: "invoice",
          invoiceUuid,
          openTimestamp:
            convertDateStringOrTimestampToUsDateFormat(openTimestamp),
          nextAttemptTimestamp:
            convertDateStringOrTimestampToUsDateFormat(nextAttemptTimestamp),
          billingLastFour,
          billingBrand,
          total: formatCurrency(total, currencyIso),
          isPending,
          isVerified,
          billingType: getBillingType(billingType, invoice),
        };
      });
    }

    if (paymentHistoryData?.length) {
      payments = paymentHistoryData.map((payment) => {
        const {
          invoiceUuid,
          transactionLogUuid,
          receiptId,
          timestamp,
          amount,
          description,
          comped,
          status,
        } = payment;
        return {
          type: "payment",
          invoiceUuid,
          date: convertDateStringOrTimestampToUsDateFormat(timestamp),
          amount: formatCurrency(Number(amount), currencyIso),
          description,
          comped,
          status,
          receiptId,
          transactionLogUuid,
        };
      });
    }
    return invoices.concat(payments);
  };

  const tableData = useMemo(
    () =>
      convertToTableData(
        outstandingInvoicesData,
        paymentHistoryData,
        client.currencyIso,
      ),
    [outstandingInvoicesData, paymentHistoryData, client.currencyIso],
  );

  const dataLoading = isFetchingOutstandingInvoices || isFetchingPaymentHistory;
  const dataError = errorOutstandingInvoices || errorPaymentHistory;
  const showPagination =
    !dataLoading && tableData.length > PAGINATION_PAGE_SIZE;

  const currentColumns =
    viewMode === "payments" ? paymentColumns : invoiceColumns;

  const refetchData = () => {
    refetchOutstandingInvoices();
    refetchPaymentHistory();
  };

  const table = useReactTable({
    data: tableData,
    columns: currentColumns,
    state: {
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-gap-4">
      <div className="tw-rounded tw-ring-1 tw-ring-black/5">
        {dataError ? (
          <ErrorState refetch={refetchData} />
        ) : dataLoading ? (
          <TableSkeleton
            numRows={PAGINATION_PAGE_SIZE}
            numCols={currentColumns.length}
          />
        ) : (
          <Table className="tw-divide-y tw-divide-neutral-300">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="tw-bg-neutral-50">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="tw-divide-y tw-divide-neutral-200">
              {table.getRowModel()?.rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="tw-py-0 tw-cursor-pointer tw-h-14"
                    onClick={() =>
                      row.original.type === "invoice"
                        ? window.open(
                            `/invoices/invoice/${row.original.invoiceUuid}`,
                            "_blank",
                            "noopener, noreferrer",
                          )
                        : window.open(
                            getClickablePaymentLink(
                              row.original.status,
                              row.original.receiptId,
                              row.original.invoiceUuid,
                              row.original.transactionLogUuid,
                            ),
                            "_blank",
                            "noopener, noreferrer",
                          )
                    }
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell key={cell.id} className="tw-py-0">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <EmptyTable
                  colLength={currentColumns.length}
                  subtitle={getEmptyTableMessage(viewMode)}
                />
              )}
            </TableBody>
          </Table>
        )}
      </div>
      {showPagination && <TablePagination table={table} />}
    </div>
  );
}
